import { Component, Optional, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { NbDialogRef, NbCardModule, NbButtonModule, NbInputModule, NbSelectModule, NbOptionModule, NbToggleModule, NbIconModule, NbAccordionModule, NbToastrService, NbDialogService, NbDatepickerModule } from '@nebular/theme';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { CommonModule, DatePipe } from '@angular/common';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { KPI } from '../../../@core/api';
import { ComplianceService } from '../../../@core/utils/compliance.service';
import { AssetsService } from '../../../@core/utils/assets.service';
import { SaveToastService } from '../../../@core/utils';
import { Observable, Subscription } from 'rxjs';
import { skip, first, debounceTime, filter, distinctUntilChanged } from 'rxjs/operators';
import { LinkedEquipmentDialogComponent } from '../linked-equipment-dialog/linked-equipment-dialog.component';
import { RightPanelDialogService } from '../../../@core/utils/right-panel-dialog.service';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';
import { currentMonthDatepickerDate, toDateValue } from '../../utils/date.utils';


@Component({
  selector: 'app-control-report-dialog',
  templateUrl: './control-report-dialog.component.html',
  styleUrls: ['./control-report-dialog.component.scss'],
  standalone: true,
  imports: [
    NbCardModule,
    NbButtonModule,
    NbInputModule,
    NbSelectModule,
    NbOptionModule,
    NbToggleModule,
    NbIconModule,
    NbAccordionModule,
    TranslateModule,
    FormsModule,
    ReactiveFormsModule,
    CommonModule,
    NbDatepickerModule,
    FontAwesomeModule
  ],
  providers: [DatePipe]
})
export class ControlReportDialogComponent implements OnInit, OnDestroy {
  public form: UntypedFormGroup | null = null;
  public reserves: Array<{ 
    id?: number; 
    date_emission: string; 
    libelle: string;
    stade?: string;
    criticite?: string;
    responsable?: string;
    date_cloture?: string;
    commentaire?: string;
  }> = [];
  public asset: KPI.MappedAsset | null = null;
  public lastControlDate: string | null = null;
  public reportId: number | null = null;
  public latestReport: KPI.ControlOfficeReport | null = null;
  public perimeterId: number | null = null;
  public isEditable: boolean = false;
  private reservationId: number | null = null;
  private complianceId: number | null = null;
  private onReportSavedCallback: (() => void) | null = null;
  private originalReserveIds: number[] = [];
  private initialFormValues: any = null;
  private initialReserves: Array<{
    id?: number;
    date_emission: string;
    libelle: string;
    stade?: string;
    criticite?: string;
    responsable?: string;
    date_cloture?: string;
    commentaire?: string;
  }> = [];
  private formSubscriptions: Subscription[] = [];
  private backdropClickSubscription: Subscription | null = null;
  private initialValuesStored: boolean = false;
  private userHasInteracted: boolean = false;
  public readonly defaultDatepickerDate = currentMonthDatepickerDate;

  constructor(
    @Optional() public ref: NbDialogRef<ControlReportDialogComponent>,
    private datePipe: DatePipe,
    private cdr: ChangeDetectorRef,
    private complianceService: ComplianceService,
    private toastrService: NbToastrService,
    private translate: TranslateService,
    private dialogService: NbDialogService,
    private assetsService: AssetsService,
    private saveToast: SaveToastService,
    private sideFormLevelService: RightPanelDialogService,
  ) {
    const context = (this.ref as any)?.context || {};
    this.asset = context.asset || null;
    this.lastControlDate = context.lastControlDate || null;
    this.reportId = context.reportId || null;
    this.latestReport = context.latestReport || null;
    this.perimeterId = context.perimeterId || null;
    this.reservationId = context.reservationId || null;
    this.onReportSavedCallback = context.onReportSaved || null;
    this.isEditable = !!this.perimeterId || (!this.asset && !this.reportId) || !!this.reportId;
    this.updateComplianceId();
  }

  private updateComplianceId(): void {
    const compliance = (this.asset as any)?.compliance;
    if (compliance) {
      const id = typeof compliance === 'object' && compliance.id ? compliance.id : compliance;
      this.complianceId = id && id > 0 ? id : null;
    } else {
      this.complianceId = null;
    }
  }

  public ngOnInit(): void {
    if (this.isEditable && !this.asset && !this.reportId) {
      this.initializeDialog();
      return;
    }
    
    if (!this.complianceId && this.asset?.id) {
      this.assetsService.getAsset(this.asset.id).pipe(first()).subscribe(updatedAsset => {
        if (updatedAsset) {
          this.asset = { ...updatedAsset as any } as KPI.MappedAsset;
          this.updateComplianceId();
        }
        this.initializeDialog();
      });
    } else {
      this.initializeDialog();
    }
  }

  private initializeDialog(): void {
    if (this.reportId) {
      this.loadReport();
    } else {
      this.loadReservesFromAsset();
      if (!this.form) {
        this.initializeForm();
      }
      setTimeout(() => {
        this.cdr.detectChanges();
        this.storeInitialValues();
      }, 0);
    }
    this.initializeBackdropClick();
  }

  private loadReport(): void {
    if (!this.reportId) return;

    this.complianceService.getControlOfficeReport(this.reportId)
      .subscribe({
        next: (report) => {
          this.latestReport = report;
          this.loadReservesFromAsset();
          this.initializeForm();
          setTimeout(() => {
            this.storeInitialValues();
          }, 0);
        },
        error: () => {
          this.toastrService.show(
            null,
            this.translate.instant("asset_form_drawer.compliance.control_report.error_loading_report"),
            { status: "danger", duration: 5000 }
          );
          this.loadReservesFromAsset();
          this.initializeForm();
          setTimeout(() => {
            this.storeInitialValues();
          }, 0);
        }
      });
  }

  private loadReservesFromAsset(): void {
    this.reserves = [];
    this.originalReserveIds = [];
    
    if (this.reportId) {
      this.complianceService.getReportReservations(this.reportId).pipe(first()).subscribe({
        next: (reservations) => {
          this.reserves = reservations.map((reservation: any) => ({
            id: reservation.id,
            date_emission: reservation.issue_date || "",
            libelle: reservation.label || "",
            stade: reservation.status || reservation.stade || "",
            criticite: reservation.criticality || reservation.criticite || "",
            responsable: reservation.responsible || reservation.responsable || "",
            date_cloture: reservation.closing_date || reservation.date_cloture || "",
            commentaire: reservation.comment || reservation.commentaire || ""
          }));
          
          this.originalReserveIds = this.reserves.filter(r => r.id).map(r => r.id!);
          this.initialReserves = this.reserves.map(reserve => {
            const normalized: any = {};
            Object.keys(reserve).forEach(key => {
              const value = reserve[key as keyof typeof reserve];
              normalized[key] = value === null || value === undefined ? '' : value;
            });
            return normalized;
          });
          this.cdr.detectChanges();
          this.scrollToReservation();
        },
        error: () => {
          this.reserves = [];
          this.originalReserveIds = [];
          this.initialReserves = [];
          this.cdr.detectChanges();
        }
      });
      return;
    }
    
    if (this.asset) {
      const compliance = (this.asset as any)?.compliance;
      if (compliance?.reservations && Array.isArray(compliance.reservations)) {
        let filteredReservations = compliance.reservations;
        
        if (this.complianceId) {
          filteredReservations = filteredReservations.filter((reservation: any) => 
            reservation.compliance === this.complianceId || 
            (reservation.compliance && typeof reservation.compliance === 'object' && reservation.compliance.id === this.complianceId)
          );
        }
        
        this.reserves = filteredReservations.map((reservation: any) => ({
          id: reservation.id,
          date_emission: reservation.issue_date || "",
          libelle: reservation.label || "",
          stade: reservation.status || reservation.stade || "",
          criticite: reservation.criticality || reservation.criticite || "",
          responsable: reservation.responsible || reservation.responsable || "",
          date_cloture: reservation.closing_date || reservation.date_cloture || "",
          commentaire: reservation.comment || reservation.commentaire || ""
        }));
        
        this.originalReserveIds = this.reserves.filter(r => r.id).map(r => r.id!);
        this.initialReserves = this.reserves.map(reserve => {
          const normalized: any = {};
          Object.keys(reserve).forEach(key => {
            const value = reserve[key as keyof typeof reserve];
            normalized[key] = value === null || value === undefined ? '' : value;
          });
          return normalized;
        });
        this.cdr.detectChanges();
        this.scrollToReservation();
      }
    }
    
    this.cdr.detectChanges();
    this.scrollToReservation();
  }

  private scrollToReservation(): void {
    if (this.reservationId && this.reserves.length > 0) {
      const reserveIndex = this.reserves.findIndex(r => r.id === this.reservationId);
      if (reserveIndex >= 0) {
        let reserveElement = document.querySelector(`[data-reserve-id="${this.reservationId}"]`);
        if (!reserveElement) {
          reserveElement = document.querySelector(`[data-reserve-index="${reserveIndex}"]`);
        }
        if (!reserveElement) {
          const allReserveHeaders = document.querySelectorAll('.reserve-header');
          if (allReserveHeaders[reserveIndex]) {
            reserveElement = allReserveHeaders[reserveIndex];
          }
        }
        
        if (reserveElement) {
          reserveElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          reserveElement.classList.add('highlight-reserve');
          setTimeout(() => {
            reserveElement!.classList.remove('highlight-reserve');
          }, 2000);
        }
      }
    }
  }

  private initializeBackdropClick(): void {
    if (this.ref && (this.ref as any).onBackdropClick) {
      if (this.backdropClickSubscription) {
        this.backdropClickSubscription.unsubscribe();
      }
      this.backdropClickSubscription = (this.ref as any).onBackdropClick.subscribe(() => {
        this.confirmClose();
      });
    }
  }


  private initializeForm(): void {
    if (this.isEditable && !this.asset && !this.reportId) {
      this.form = new UntypedFormGroup({
        libelle_rapport: new UntypedFormControl(''),
        date_controle: new UntypedFormControl(null),
        url: new UntypedFormControl(''),
        frequence: new UntypedFormControl('1 fois par an'),
        equipements_lies: new UntypedFormControl('-'),
        description: new UntypedFormControl(''),
        commentaire: new UntypedFormControl(''),
        date_derniere: new UntypedFormControl(null),
        equipement_conforme: new UntypedFormControl(true)
      });
      this.form.enable();
      this.cdr.detectChanges();
      return;
    }

    const initialDateControle = this.lastControlDate || null;
    const compliance = (this.asset as any)?.compliance || {};
    const controlReport = this.latestReport || (this.asset as any)?.control_office_report || compliance.control_office_report || {};
    let linkedEquipmentCount = 0;
    if (controlReport.linked_equipment && Array.isArray(controlReport.linked_equipment)) {
      linkedEquipmentCount = controlReport.linked_equipment.length;
    }
    
    const normalizedFrequence = compliance.control_frequency || '1 fois par an';
    const normalizedEquipementConforme = compliance.environmental_compliance !== undefined ? compliance.environmental_compliance : true;
    const shouldPreFillDate = this.reportId !== null && this.reportId !== undefined;
    
    let dateValue = null;
    if (controlReport.date) {
      const dateStr = typeof controlReport.date === 'string' 
        ? controlReport.date 
        : this.datePipe.transform(controlReport.date, "yyyy-MM-dd");
      if (dateStr) {
        const date = new Date(dateStr);
        if (!isNaN(date.getTime())) {
          dateValue = date;
        }
      }
    } else if (shouldPreFillDate && this.reportId) {
      const dateStr = initialDateControle || compliance.last_control_date || null;
      if (dateStr) {
        const date = new Date(dateStr);
        if (!isNaN(date.getTime())) {
          dateValue = date;
        }
      }
    }
    
    this.form = new UntypedFormGroup({
      libelle_rapport: new UntypedFormControl(controlReport.label || compliance.libelle_rapport || ''),
      date_controle: new UntypedFormControl(dateValue),
      url: new UntypedFormControl(controlReport.url || compliance.url || ''),
      frequence: new UntypedFormControl(normalizedFrequence),
      equipements_lies: new UntypedFormControl(linkedEquipmentCount > 0 ? String(linkedEquipmentCount) : '-'),
      description: new UntypedFormControl(compliance.control_description || ''),
      commentaire: new UntypedFormControl(controlReport.comment || compliance.commentaire || ''),
      date_derniere: new UntypedFormControl(initialDateControle || compliance.last_control_date || null),
      equipement_conforme: new UntypedFormControl(normalizedEquipementConforme)
    });
    
    if (this.isEditable) {
      this.form.enable();
      setTimeout(() => {
        const urlValue = this.form?.get('url')?.value || '';
        if (urlValue.length > 100) {
          const urlInput = document.querySelector('input[formControlName="url"]') as HTMLInputElement;
          if (urlInput) {
            urlInput.value = this.getFormattedUrl(urlValue);
          }
        }
      }, 0);
    }
    
    if (this.initialValuesStored) {
      this.updateReportFormDisabledState();
    }
    
    this.cdr.detectChanges();
  }

  private subscribeToFormChanges(): void {
    this.formSubscriptions.forEach(sub => sub.unsubscribe());
    this.formSubscriptions = [];
    if (!this.initialValuesStored || !this.form) {
      return;
    }

    Object.keys(this.form.controls).forEach(key => {
      if (key === 'url' && this.reportId) {
        return;
      }
      
      if (key === 'date_controle') {
        return;
      }
      
      const control = this.form.get(key);
      if (control) {
        const subscription = control.valueChanges.pipe(skip(1)).subscribe(() => {
          this.markUserInteraction();
        });
        this.formSubscriptions.push(subscription);
      }
    });
    
    if (this.isEditable) {
      const dateControl = this.form.get('date_controle');
      if (dateControl) {
        const subscription = dateControl.valueChanges.pipe(
          skip(1),
          filter(() => {
            if (!this.initialFormValues) return true;
            const currentValue = dateControl.value;
            const initialValue = this.initialFormValues.date_controle;
            
            const normalizedCurrent = this.normalizeFormValue(currentValue);
            const normalizedInitial = this.normalizeFormValue(initialValue);
            
            return normalizedCurrent !== normalizedInitial;
          })
        ).subscribe(() => {
          this.markUserInteraction();
        });
        this.formSubscriptions.push(subscription);
      }

      const urlControl = this.form.get('url');
      if (urlControl && this.reportId) {
        const subscription = urlControl.valueChanges.pipe(
          debounceTime(800),
          filter(() => this.reportId !== null && !!this.reportId),
          filter((urlValue) => {
            const currentValue = (urlValue || '').trim();
            const initialValue = (this.initialFormValues?.url || '').trim();
            return currentValue !== initialValue;
          }),
          distinctUntilChanged()
        ).subscribe((urlValue) => {
          this.autoSaveUrl(urlValue);
        });
        this.formSubscriptions.push(subscription);
      }
    }
  }

  private normalizeFormValue(value: any): string | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    if (value && typeof value === 'object' && (value as any)._isAMomentObject) {
      return (value as any).format('YYYY-MM-DD');
    }

    if (value instanceof Date) {
      return this.datePipe.transform(value, "yyyy-MM-dd") || null;
    }

    if (typeof value === 'string') {
      if (value.match(/^\d{4}-\d{2}-\d{2}/)) {
        return value;
      }

      try {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          const normalized = this.datePipe.transform(date, "yyyy-MM-dd");
          if (normalized) {
            return normalized;
          }
        }
      } catch (e) {
      }

      return value;
    }

    return String(value);
  }

  private storeInitialValues(): void {
    if (this.form) {
      this.initialFormValues = {};
      Object.keys(this.form.controls).forEach(key => {
        const control = this.form.get(key);
        if (control) {
          const normalized = this.normalizeFormValue(control.value);
          this.initialFormValues[key] = normalized;
        }
      });
    }

    this.initialReserves = this.reserves.map(reserve => {
      const normalized: any = {};
      Object.keys(reserve).forEach(key => {
        const value = reserve[key as keyof typeof reserve];
        normalized[key] = value === null || value === undefined ? '' : value;
      });
      return normalized;
    });
    
    this.initialValuesStored = true;
    
    this.subscribeToFormChanges();
    this.updateReportFormDisabledState();
    
    setTimeout(() => {
      this.cdr.detectChanges();
    }, 0);
  }

  public markUserInteraction(): void {
    this.userHasInteracted = true;
    this.updateReportFormDisabledState();
    this.updateReserveFormDisabledState();
    this.cdr.detectChanges();
  }

  private reloadAssetAndReserves(): void {
    if (this.asset?.id) {
      this.assetsService.getAsset(this.asset.id).pipe(first()).subscribe(updatedAsset => {
        if (updatedAsset) {
          this.asset = { ...updatedAsset as any } as KPI.MappedAsset;
          this.updateComplianceId();
          this.loadReservesFromAsset();
          
          this.initializeForm();
          
          setTimeout(() => {
            this.initialValuesStored = false;
            this.userHasInteracted = false;
            this.storeInitialValues();
          }, 0);
          
          this.cdr.detectChanges();
        }
      });
    } else if (this.reportId) {
      this.complianceService.getReportReservations(this.reportId).pipe(first()).subscribe({
        next: (reservations) => {
          this.reserves = reservations.map((reservation: any) => ({
            id: reservation.id,
            date_emission: reservation.issue_date || "",
            libelle: reservation.label || "",
            stade: reservation.status || reservation.stade || "",
            criticite: reservation.criticality || reservation.criticite || "",
            responsable: reservation.responsible || reservation.responsable || "",
            date_cloture: reservation.closing_date || reservation.date_cloture || "",
            commentaire: reservation.comment || reservation.commentaire || ""
          }));
          
          this.originalReserveIds = this.reserves.filter(r => r.id).map(r => r.id!);
          this.initialReserves = this.reserves.map(r => ({ ...r }));
          this.initialValuesStored = false;
          this.userHasInteracted = false;
          this.storeInitialValues();
          this.cdr.detectChanges();
        },
        error: () => {
          this.reserves = [];
          this.originalReserveIds = [];
          this.initialReserves = [];
          this.initialValuesStored = false;
          this.userHasInteracted = false;
          this.storeInitialValues();
        }
      });
    } else {
      this.initialValuesStored = false;
      this.userHasInteracted = false;
      this.storeInitialValues();
    }
  }

  private handleSaveSuccess(): void {
    if (this.onReportSavedCallback) {
      this.onReportSavedCallback();
    }
  }

  private showErrorToast(message: string): void {
    this.toastrService.show(
      null,
      this.translate.instant(message),
      {
        status: "danger",
        duration: 5000
      }
    );
  }

  private showSuccessToast(): void {
    if (this.isEditable) {
      this.saveToast.showSaved();
    }
  }

  public addReserve(): void {
    this.markUserInteraction();
    let reportDate = "";
    if (this.latestReport?.created_at) {
      const date = this.latestReport.created_at;
      if (typeof date === 'string') {
        reportDate = date.split('T')[0];
      } else if (date && typeof date === 'object') {
        if ((date as any)._isAMomentObject) {
          reportDate = (date as any).format('YYYY-MM-DD');
        } else if (typeof (date as any).getTime === 'function') {
          reportDate = this.datePipe.transform(date as Date, 'yyyy-MM-dd') || "";
        }
      }
    } else if (this.form) {
      const dateControl = this.form.get('date_controle');
      if (dateControl && dateControl.value) {
        const dateValue = dateControl.value;
        if (dateValue instanceof Date) {
          reportDate = this.datePipe.transform(dateValue, 'yyyy-MM-dd') || "";
        } else if (typeof dateValue === 'string') {
          reportDate = dateValue.split('T')[0];
        } else if (dateValue && typeof dateValue === 'object' && (dateValue as any)._isAMomentObject) {
          reportDate = (dateValue as any).format('YYYY-MM-DD');
        }
      }
      
      if (!reportDate) {
        reportDate = this.lastControlDate || "";
      }
    } else {
      reportDate = this.lastControlDate || "";
    }
    
    this.reserves.push({
      date_emission: reportDate,
      libelle: "",
      stade: "active",
      criticite: "",
      responsable: "",
      date_cloture: "",
      commentaire: ""
    });
    
    this.updateReportFormDisabledState();
    this.cdr.detectChanges();
  }

  public deleteReserve(index: number): void {
    this.markUserInteraction();
    this.reserves.splice(index, 1);
    this.updateReportFormDisabledState();
    this.cdr.detectChanges();
  }

  public onReserveLabelChange(index: number, event: Event): void {
    this.markUserInteraction();
    const target = event.target as HTMLInputElement;
    if (this.reserves[index]) {
      this.reserves[index].libelle = target.value || "";
    }
    this.updateReportFormDisabledState();
    this.cdr.detectChanges();
  }

  public onReserveStadeChange(index: number, value: string): void {
    this.markUserInteraction();
    if (this.reserves[index]) {
      const oldStatus = this.reserves[index].stade;
      this.reserves[index].stade = value || "";
      
      if (value === "closed" && oldStatus !== "closed") {
        const today = new Date();
        const todayStr = this.datePipe.transform(today, "yyyy-MM-dd") || "";
        this.reserves[index].date_cloture = todayStr;
      }
    }
    this.updateReportFormDisabledState();
    this.cdr.detectChanges();
  }

  public onReserveCriticiteChange(index: number, value: string): void {
    this.markUserInteraction();
    if (this.reserves[index]) {
      this.reserves[index].criticite = value || "";
    }
    this.updateReportFormDisabledState();
    this.cdr.detectChanges();
  }

  public onReserveResponsableChange(index: number, event: Event): void {
    this.markUserInteraction();
    const target = event.target as HTMLInputElement;
    if (this.reserves[index]) {
      this.reserves[index].responsable = target.value || "";
    }
    this.updateReportFormDisabledState();
    this.cdr.detectChanges();
  }

  public onReserveClotureDateChange(date: Date, index: number): void {
    this.markUserInteraction();
    if (this.reserves[index]) {
      if (date) {
        this.reserves[index].date_cloture = this.datePipe.transform(date, "yyyy-MM-dd") || "";
      } else {
        this.reserves[index].date_cloture = "";
      }
    }
    this.updateReportFormDisabledState();
    this.cdr.detectChanges();
  }

  public onReserveCommentaireChange(index: number, event: Event): void {
    this.markUserInteraction();
    const target = event.target as HTMLInputElement | HTMLTextAreaElement;
    if (this.reserves[index]) {
      this.reserves[index].commentaire = target.value || "";
    }
  }

  public onDateChange(date: Date, fieldName: string): void {
    this.form.get(fieldName)?.setValue(date ?? null);
  }

  public getFormattedDate(fieldName: string): string {
    if (!this.form) return '';
    const dateValue = this.form.get(fieldName)?.value;
    if (!dateValue) return '';
    
    if (dateValue instanceof Date) {
      return this.datePipe.transform(dateValue, "dd MMM yyyy", "fr-FR") || '';
    }
    
    if (typeof dateValue === 'string') {
      if (dateValue.includes(' ')) {
        return dateValue;
      }
      const date = toDateValue(dateValue);
      if (date) {
        return this.datePipe.transform(date, "dd MMM yyyy", "fr-FR") || '';
      }
    }
    
    return '';
  }

  public getDateValue(dateValue: unknown): Date | null {
    return dateValue instanceof Date || typeof dateValue === 'string' ? toDateValue(dateValue) : null;
  }

  public getFormattedUrl(url: string | null | undefined): string {
    if (!url) return '';
    if (url.length > 100) {
      return url.substring(0, 100) + ' (..)';
    }
    return url;
  }

  public onUrlFocus(event: FocusEvent): void {
    const input = event.target as HTMLInputElement;
    if (this.form && input.value.includes(' (..)')) {
      input.value = this.form.get('url')?.value || '';
    }
  }

  public onUrlBlur(event: FocusEvent): void {
    const input = event.target as HTMLInputElement;
    if (this.form) {
      const urlValue = input.value.replace(' (..)', '');
      this.form.get('url')?.setValue(urlValue, { emitEvent: false });
      if (urlValue.length > 100) {
        input.value = this.getFormattedUrl(urlValue);
      }
    }
  }

  public openUrl(url: string | null | undefined, event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (!url) return;
    let urlToOpen = url.trim();
    if (!urlToOpen.match(/^https?:\/\//i)) {
      urlToOpen = 'https://' + urlToOpen;
    }
    window.open(urlToOpen, '_blank', 'noopener,noreferrer');
  }


  public getReserveDateValue(dateString: string | null | undefined): Date | null {
    return toDateValue(dateString);
  }

  public getFormattedReserveDate(dateString: string | null | undefined): string {
    const date = this.getReserveDateValue(dateString);
    return date ? this.datePipe.transform(date, "dd MMM yyyy", "fr-FR") || '' : '';
  }

  public openLinkedEquipmentDialog(): void {
    const linkedEquipmentIds = this.latestReport?.linked_equipment || [];
    
    let siteId: number | null = null;
    if (this.perimeterId) {
      siteId = this.perimeterId;
    } else if (this.asset?.perimeters && this.asset.perimeters.length > 0) {
      const site = this.asset.perimeters.find(p => !p.level_parent || !p.is_monosite) 
        || this.asset.perimeters[0];
      siteId = site.id;
    }
    
    const dialogRef = this.sideFormLevelService.open(LinkedEquipmentDialogComponent, {
      autoFocus: false,
      closeOnBackdropClick: false,
      hasScroll: false,
      hasBackdrop: true,
      backdropClass: 'control-report-backdrop',
      dialogClass: 'linked-equipment-dialog-overlay',
      context: {
        linkedEquipmentIds: linkedEquipmentIds,
        asset: this.asset,
        reportId: this.reportId,
        siteId: siteId
      } as any
    });

    dialogRef.onClose.pipe(first()).subscribe(() => {
      if (this.reportId) {
        this.complianceService.getControlOfficeReport(this.reportId).pipe(first()).subscribe({
          next: (updatedReport) => {
            this.latestReport = updatedReport;
            const linkedEquipmentCount = updatedReport.linked_equipment && Array.isArray(updatedReport.linked_equipment)
              ? updatedReport.linked_equipment.length
              : 0;
            
            if (this.form) {
              const equipementsLiesControl = this.form.get('equipements_lies');
              if (equipementsLiesControl) {
                equipementsLiesControl.setValue(linkedEquipmentCount > 0 ? String(linkedEquipmentCount) : '-', { emitEvent: false });
              }
            }
            
            this.loadReservesFromAsset();
            setTimeout(() => {
              this.storeInitialValues();
            }, 0);
            
            this.cdr.detectChanges();
          },
          error: () => {
          }
        });
      }
    });
  }

  public getFieldBackgroundColor(fieldName: string): string {
    if (!this.form) return '';
    
    const value = this.form.get(fieldName)?.value;
    
    switch (fieldName) {
      case 'libelle_rapport':
      case 'date_controle':
        if (fieldName === 'date_controle') {
          return (!value || value === null) ? '#FCEDE4' : '';
        }
        return (!value || value === '' || value === null) ? '#FCEDE4' : '';
      default:
        return '';
    }
  }

  public getReserveFieldBackgroundColor(index: number, fieldName: string): string {
    if (index < 0 || index >= this.reserves.length) return '';
    
    const reserve = this.reserves[index];
    if (!reserve) return '';
    
    switch (fieldName) {
      case 'libelle':
        return (!reserve.libelle || reserve.libelle.trim() === '') ? '#FCEDE4' : '';
      case 'stade':
        return (!reserve.stade || reserve.stade.trim() === '') ? '#FCEDE4' : '';
      default:
        return '';
    }
  }

  public hasReserves(): boolean {
    return this.reserves.length > 0;
  }

  public isReportFormDisabled(): boolean {
    if (!this.hasReserves()) {
      return false;
    }
    
    if (!this.initialValuesStored) {
      return false;
    }
    
    if (this.reserves.length !== this.initialReserves.length) {
      return true;
    }
    
    for (let i = 0; i < this.reserves.length; i++) {
      const current = this.reserves[i];
      const initial = this.initialReserves[i];
      
      const normalizeReserveValue = (val: any): string => {
        if (val === null || val === undefined || val === '') return '';
        return String(val).trim();
      };
      
      if (!initial) {
        return true;
      }
      
      if (
        normalizeReserveValue(current.libelle) !== normalizeReserveValue(initial.libelle) ||
        normalizeReserveValue(current.stade) !== normalizeReserveValue(initial.stade) ||
        normalizeReserveValue(current.criticite) !== normalizeReserveValue(initial.criticite) ||
        normalizeReserveValue(current.responsable) !== normalizeReserveValue(initial.responsable) ||
        normalizeReserveValue(current.date_cloture) !== normalizeReserveValue(initial.date_cloture) ||
        normalizeReserveValue(current.commentaire) !== normalizeReserveValue(initial.commentaire)
      ) {
        return true;
      }
    }
    
    return false;
  }

  private updateReportFormDisabledState(): void {
    if (!this.form) return;
    
    const shouldDisable = this.isReportFormDisabled();
    
    if (this.isEditable) {
      const reportFields = ['libelle_rapport', 'date_controle', 'url', 'commentaire'];
      reportFields.forEach(fieldName => {
        const control = this.form.get(fieldName);
        if (control) {
          if (shouldDisable) {
            control.disable({ emitEvent: false });
          } else {
            control.enable({ emitEvent: false });
          }
        }
      });
    }
    
    this.updateReserveFormDisabledState();
  }

  public isReserveFormDisabled(): boolean {
    if (!this.initialValuesStored || !this.form) {
      return false;
    }
    
    return this.hasReportFormChanges();
  }

  private updateReserveFormDisabledState(): void {
    this.cdr.detectChanges();
  }

  public canSave(): boolean {
    if (!this.form) return false;
    const libelle = this.form.get('libelle_rapport')?.value;
    const date = this.form.get('date_controle')?.value;
    if (!libelle || !libelle.trim() || !date) {
      return false;
    }
    
    if (this.reserves.length > 0) {
      const invalidReserves = this.reserves.filter(r => 
        !r.libelle || r.libelle.trim() === "" || !r.stade || r.stade.trim() === ""
      );
      if (invalidReserves.length > 0) {
        return false;
      }
    }
    
    return true;
  }

  public hasChanges(): boolean {
    if (this.isEditable) {
      if (!this.userHasInteracted) {
        return false;
      }
      return this.canSave();
    }
    
    if (!this.initialValuesStored) {
      return false;
    }
    
    if (!this.userHasInteracted) {
      return false;
    }
    
    if (!this.initialFormValues || !this.form) {
      return false;
    }
    
    if (this.form && this.initialFormValues) {
      const formChanged = Object.keys(this.initialFormValues).some(key => {
        if (key === 'url' && this.reportId) {
          return false;
        }
        
        if (key === 'equipements_lies') {
          return false;
        }
        
        const currentValue = this.form.get(key)?.value;
        const initialValue = this.initialFormValues[key];
        
        const normalizedCurrent = this.normalizeFormValue(currentValue);
        const normalizedInitial = this.normalizeFormValue(initialValue);
        
        if (normalizedCurrent === null && normalizedInitial === null) {
          return false;
        }
        
        return normalizedCurrent !== normalizedInitial;
      });

      if (formChanged) {
        return true;
      }
    }

    if (this.reserves.length !== this.initialReserves.length) {
      return true;
    }

    for (let i = 0; i < this.reserves.length; i++) {
      const current = this.reserves[i];
      const initial = this.initialReserves[i];

      const normalizeReserveValue = (val: any): string => {
        if (val === null || val === undefined || val === '') return '';
        return String(val).trim();
      };

      if (!initial) {
        const hasLibelle = normalizeReserveValue(current.libelle);
        const hasStade = normalizeReserveValue(current.stade);
        if (hasLibelle && hasStade) {
          return true;
        }
        continue;
      }

      if (
        normalizeReserveValue(current.libelle) !== normalizeReserveValue(initial.libelle) ||
        normalizeReserveValue(current.stade) !== normalizeReserveValue(initial.stade) ||
        normalizeReserveValue(current.criticite) !== normalizeReserveValue(initial.criticite) ||
        normalizeReserveValue(current.responsable) !== normalizeReserveValue(initial.responsable) ||
        normalizeReserveValue(current.date_cloture) !== normalizeReserveValue(initial.date_cloture) ||
        normalizeReserveValue(current.commentaire) !== normalizeReserveValue(initial.commentaire)
      ) {
        return true;
      }
    }

    const currentReserveIds = this.reserves.filter(r => r.id).map(r => r.id!);
    const initialReserveIds = this.initialReserves.filter(r => r.id).map(r => r.id!);
    if (currentReserveIds.length !== initialReserveIds.length) {
      return true;
    }
    if (currentReserveIds.some(id => !initialReserveIds.includes(id))) {
      return true;
    }

    return false;
  }

  public save(): void {
    const invalidReserves = this.reserves.filter(r =>
      !r.libelle || r.libelle.trim() === "" || !r.stade || r.stade.trim() === ""
    );
    if (invalidReserves.length > 0) {
      this.showErrorToast(this.translate.instant("asset_form_drawer.compliance.control_report.error_fill_reserve_fields"));
      return;
    }

    this.saveToast.showSaving();
    const reportHasChanges = this.hasReportFormChanges();
    const reserveHasChanges = this.hasReserveChanges();
    const needsReportCreation = !this.reportId;

    if (needsReportCreation || reportHasChanges) {
      this.saveReport().subscribe({
        next: (savedReport) => {
          const wasNewReport = !this.reportId;
          
          if (wasNewReport && savedReport) {
            this.reportId = savedReport.id;
            this.latestReport = savedReport;
          }
          
          if (wasNewReport) {
            if (reserveHasChanges) {
              this.saveReservationsInternal();
            } else {
              this.showSuccessToast();
              this.handleSaveSuccess();
              this.reloadAssetAndReserves();
            }
          } else if (this.reportId) {
            this.complianceService.getControlOfficeReport(this.reportId).pipe(first()).subscribe({
              next: (updatedReport) => {
                this.latestReport = updatedReport;
                
                if (reserveHasChanges) {
                  this.saveReservationsInternal();
                } else {
                  this.showSuccessToast();
                  this.handleSaveSuccess();
                  this.reloadAssetAndReserves();
                }
              },
              error: () => {
                if (reserveHasChanges) {
                  this.saveReservationsInternal();
                } else {
                  this.showSuccessToast();
                  this.handleSaveSuccess();
                  this.reloadAssetAndReserves();
                }
              }
            });
          } else {
            if (reserveHasChanges) {
              this.saveReservationsInternal();
            } else {
              this.showSuccessToast();
              this.handleSaveSuccess();
              this.reloadAssetAndReserves();
            }
          }
        },
        error: () => {
          this.showErrorToast(this.translate.instant("asset_form_drawer.compliance.control_report.error_saving_report"));
        }
      });
    } else if (reserveHasChanges) {
      this.saveReservationsInternal();
    }
  }

  private hasReportFormChanges(): boolean {
    if (!this.initialFormValues || !this.form) {
      return false;
    }
    
    return Object.keys(this.initialFormValues).some(key => {
      // Exclude url from change detection when reportId exists
      if (key === 'url' && this.reportId) {
        return false;
      }
      
      if (key === 'equipements_lies') {
        return false;
      }
      
      const currentValue = this.form.get(key)?.value;
      const initialValue = this.initialFormValues[key];
      
      const normalizedCurrent = this.normalizeFormValue(currentValue);
      const normalizedInitial = this.normalizeFormValue(initialValue);
      
      if (normalizedCurrent === null && normalizedInitial === null) {
        return false;
      }
      
      return normalizedCurrent !== normalizedInitial;
    });
  }

  public hasReserveChanges(): boolean {
    if (!this.initialValuesStored) {
      return false;
    }

    if (this.reserves.length !== this.initialReserves.length) {
      return true;
    }

    for (let i = 0; i < this.reserves.length; i++) {
      const current = this.reserves[i];
      const initial = this.initialReserves[i];

      const normalizeReserveValue = (val: any): string => {
        if (val === null || val === undefined || val === '') return '';
        return String(val).trim();
      };

      if (!initial) {
        const hasLibelle = normalizeReserveValue(current.libelle);
        const hasStade = normalizeReserveValue(current.stade);
        if (hasLibelle && hasStade) {
          return true;
        }
        continue;
      }

      if (
        normalizeReserveValue(current.libelle) !== normalizeReserveValue(initial.libelle) ||
        normalizeReserveValue(current.stade) !== normalizeReserveValue(initial.stade) ||
        normalizeReserveValue(current.criticite) !== normalizeReserveValue(initial.criticite) ||
        normalizeReserveValue(current.responsable) !== normalizeReserveValue(initial.responsable) ||
        normalizeReserveValue(current.date_cloture) !== normalizeReserveValue(initial.date_cloture) ||
        normalizeReserveValue(current.commentaire) !== normalizeReserveValue(initial.commentaire)
      ) {
        return true;
      }
    }

    const currentReserveIds = this.reserves.filter(r => r.id).map(r => r.id!);
    const initialReserveIds = this.initialReserves.filter(r => r.id).map(r => r.id!);
    if (currentReserveIds.length !== initialReserveIds.length) {
      return true;
    }
    if (currentReserveIds.some(id => !initialReserveIds.includes(id))) {
      return true;
    }

    return false;
  }

  private saveReservationsInternal(): void {
    this.saveReservations().subscribe({
      next: () => {
        if (this.reportId) {
          this.complianceService.getControlOfficeReport(this.reportId).pipe(first()).subscribe({
            next: (updatedReport) => {
              this.latestReport = updatedReport;
              this.showSuccessToast();
              this.handleSaveSuccess();
              this.reloadAssetAndReserves();
            },
            error: () => {
              this.showSuccessToast();
              this.handleSaveSuccess();
              this.reloadAssetAndReserves();
            }
          });
        } else {
          this.showSuccessToast();
          this.handleSaveSuccess();
          this.reloadAssetAndReserves();
        }
      },
      error: () => {
        this.showErrorToast(this.translate.instant("asset_form_drawer.compliance.control_report.error_saving_reserves"));
      }
    });
  }

  private autoSaveUrl(urlValue: string): void {
    if (!this.reportId) {
      return;
    }

    if (urlValue && urlValue.length > 200) {
      this.showErrorToast("asset_form_drawer.compliance.control_report.url_too_long");
      const previousUrl = this.initialFormValues?.url || '';
      this.form?.get('url')?.setValue(previousUrl, { emitEvent: false });
      return;
    }

    const normalizedUrl = (urlValue && urlValue.trim()) ? urlValue.trim() : null;
    const payload: Partial<KPI.ControlOfficeReportUpdatePayload> = {
      url: normalizedUrl
    };

    this.complianceService.patchControlOfficeReport(this.reportId, payload).subscribe({
      next: (updatedReport) => {
        this.latestReport = updatedReport;
        if (this.initialFormValues) {
          this.initialFormValues.url = urlValue || null;
        }
        this.showSuccessToast();
        this.cdr.detectChanges();
      },
      error: (error) => {
        let errorMessage = this.translate.instant("asset_form_drawer.compliance.control_report.error_saving_report");
        if (error?.error) {
          const errorText = typeof error.error === 'string' ? error.error : JSON.stringify(error.error);
          if (errorText.includes('200 caractères') || errorText.includes('200 characters')) {
            errorMessage = this.translate.instant("asset_form_drawer.compliance.control_report.url_too_long");
            const previousUrl = this.initialFormValues?.url || '';
            this.form?.get('url')?.setValue(previousUrl, { emitEvent: false });
          } else if (error.error?.url) {
            errorMessage = Array.isArray(error.error.url) ? error.error.url[0] : error.error.url;
          }
        }
        this.showErrorToast(errorMessage);
      }
    });
  }

  private saveReport(): Observable<KPI.ControlOfficeReport> {
    const formValue = this.form.value;
    let dateControle: string | null = null;
    
    if (formValue.date_controle) {
      if (formValue.date_controle && typeof formValue.date_controle === 'object' && (formValue.date_controle as any)._isAMomentObject) {
        dateControle = (formValue.date_controle as any).format('YYYY-MM-DD');
      } else if (formValue.date_controle instanceof Date) {
        dateControle = this.datePipe.transform(formValue.date_controle, "yyyy-MM-dd") || null;
      } else if (typeof formValue.date_controle === 'string') {
        if (formValue.date_controle.match(/^\d{4}-\d{2}-\d{2}/)) {
          dateControle = formValue.date_controle;
        } else {
          const date = toDateValue(formValue.date_controle);
          if (date) {
            dateControle = this.datePipe.transform(date, "yyyy-MM-dd") || null;
          }
        }
      }
    }

    if (!dateControle || !formValue.libelle_rapport) {
      return new Observable(observer => {
        observer.error(new Error(this.translate.instant("asset_form_drawer.compliance.control_report.error_date_label_required")));
        observer.complete();
      });
    }

    let perimeterId: number | undefined = undefined;
    if (this.perimeterId) {
      perimeterId = this.perimeterId;
    } else if (this.asset?.perimeters && this.asset.perimeters.length > 0) {
      const site = this.asset.perimeters.find(p => !p.level_parent || !p.is_monosite) 
        || this.asset.perimeters[0];
      perimeterId = site.id;
    } else if (this.reportId && this.latestReport?.perimeter) {
      perimeterId = this.latestReport.perimeter;
    }

    if (this.reportId) {
      const updatePayload: KPI.ControlOfficeReportUpdatePayload = {
        label: formValue.libelle_rapport,
        date: dateControle,
        url: formValue.url || undefined,
        comment: formValue.commentaire || undefined
      };
      
      if (perimeterId !== undefined) {
        updatePayload.perimeter = perimeterId;
      }
      
      if (this.asset?.id) {
        const existingLinked = this.latestReport?.linked_equipment || [];
        if (!existingLinked.includes(this.asset.id)) {
          updatePayload.linked_equipment = [...existingLinked, this.asset.id];
        } else {
          updatePayload.linked_equipment = existingLinked;
        }
      } else {
        if (this.latestReport?.linked_equipment) {
          updatePayload.linked_equipment = this.latestReport.linked_equipment;
        }
      }
      
      return this.complianceService.updateControlOfficeReport(this.reportId, updatePayload);
    } else {
      const createPayload: KPI.ControlOfficeReportCreatePayload = {
        label: formValue.libelle_rapport,
        date: dateControle,
        url: formValue.url || undefined,
        comment: formValue.commentaire || undefined,
        linked_equipment: this.asset?.id ? [this.asset.id] : undefined
      };
      
      if (perimeterId !== undefined) {
        createPayload.perimeter = perimeterId;
      }
      
      return this.complianceService.createControlOfficeReport(createPayload);
    }
  }

  private saveReservations(): Observable<any> {
    const newReserves = this.reserves.filter(reserve => !reserve.id);
    const existingReserves = this.reserves.filter(reserve => reserve.id);
    const deletedReserveIds: number[] = [];

    const originalReserveIds = this.getOriginalReserveIds();
    const currentReserveIds = this.reserves.filter(r => r.id).map(r => r.id!);
    deletedReserveIds.push(...originalReserveIds.filter(id => !currentReserveIds.includes(id)));

    const operations: Observable<any>[] = [];

    if (this.asset) {
      this.updateComplianceId();
      
      if (!this.complianceId) {
        return new Observable(observer => {
          observer.error(new Error("Cannot save reserves: complianceId is missing"));
          observer.complete();
        });
      }

      if (newReserves.length > 0) {
        const reservationsPayload = this.complianceService.mapReservationsToPayload(newReserves);
        
        reservationsPayload.forEach(res => {
          res.compliance = this.complianceId!;
        });
        
        const createPayload: KPI.ReservationsBulkCreatePayload = {
          asset_id: this.asset.id,
          control_office_report_id: this.reportId || undefined,
          reservations: reservationsPayload
        };
        
        operations.push(this.complianceService.bulkCreateReservations(createPayload));
      }

      if (existingReserves.length > 0) {
        const changedReserves = this.getChangedReserves(existingReserves);
        
        if (changedReserves.length > 0) {
          const updatePayloads = this.complianceService.mapReservationsToUpdatePayload(changedReserves);
          
          updatePayloads.forEach(payload => {
            payload.data.compliance = this.complianceId!;
            if (this.reportId) {
              payload.data.control_office_report = this.reportId;
            }
          });
          
          operations.push(this.complianceService.bulkUpdateReservations(updatePayloads));
        }
      }
    } else if (this.reportId && this.perimeterId) {
      if (newReserves.length > 0) {
        const reservationsPayload = this.complianceService.mapReservationsToPayload(newReserves);
        
        const createPayload: KPI.ReservationsBulkCreatePayload = {
          asset_id: null,
          perimeter_id: this.perimeterId,
          control_office_report_id: this.reportId,
          reservations: reservationsPayload
        };
        
        operations.push(this.complianceService.bulkCreateReservations(createPayload));
      }

      if (existingReserves.length > 0) {
        const changedReserves = this.getChangedReserves(existingReserves);
        
        if (changedReserves.length > 0) {
          const updatePayloads = this.complianceService.mapReservationsToUpdatePayload(changedReserves);
          
          updatePayloads.forEach(payload => {
            if (this.perimeterId) {
              payload.data.perimeter = this.perimeterId;
            }
            if (this.reportId) {
              payload.data.control_office_report = this.reportId;
            }
          });
          
          operations.push(this.complianceService.bulkUpdateReservations(updatePayloads));
        }
      }
    } else {
      return new Observable(observer => {
        observer.error(new Error("Cannot save reserves: missing required information (asset, reportId, or perimeterId)"));
        observer.complete();
      });
    }

    if (deletedReserveIds.length > 0) {
      operations.push(this.complianceService.bulkDeleteReservations(deletedReserveIds));
    }

    if (operations.length === 0) {
      return new Observable(observer => {
        observer.next(null);
        observer.complete();
      });
    }

    return new Observable(observer => {
      let completedOperations = 0;
      const results: any[] = [];

      operations.forEach((operation, index) => {
        operation.subscribe({
          next: result => {
            results[index] = result;
            completedOperations++;

            if (completedOperations === operations.length) {
              observer.next(results);
              observer.complete();
            }
          },
          error: error => {
            observer.error(error);
          }
        });
      });
    });
  }

  private getOriginalReserveIds(): number[] {
    return this.originalReserveIds;
  }

  private getChangedReserves(existingReserves: Array<{ 
    id?: number; 
    date_emission: string; 
    libelle: string;
    stade?: string;
    criticite?: string;
    responsable?: string;
    date_cloture?: string;
    commentaire?: string;
  }>): Array<{ 
    id?: number; 
    date_emission: string; 
    libelle: string;
    stade?: string;
    criticite?: string;
    responsable?: string;
    date_cloture?: string;
    commentaire?: string;
  }> {
    const normalizeReserveValue = (val: any): string => {
      if (val === null || val === undefined || val === '') return '';
      return String(val).trim();
    };

    return existingReserves.filter(current => {
      if (!current.id) {
        return false;
      }
      
      const initial = this.initialReserves.find(init => init.id === current.id);
      
      if (!initial) {
        return false;
      }

      return (
        normalizeReserveValue(current.libelle) !== normalizeReserveValue(initial.libelle) ||
        normalizeReserveValue(current.stade) !== normalizeReserveValue(initial.stade) ||
        normalizeReserveValue(current.criticite) !== normalizeReserveValue(initial.criticite) ||
        normalizeReserveValue(current.responsable) !== normalizeReserveValue(initial.responsable) ||
        normalizeReserveValue(current.date_cloture) !== normalizeReserveValue(initial.date_cloture) ||
        normalizeReserveValue(current.commentaire) !== normalizeReserveValue(initial.commentaire)
      );
    });
  }

  public cancel(): void {
    this.confirmClose();
  }

  private confirmClose(): void {
    if (this.hasChanges()) {
      this.dialogService.open(
        ConfirmationDialogComponent,
        {
          autoFocus: false,
          closeOnBackdropClick: false,
          context: {
            title: this.translate.instant('Save modifications'),
            label: this.translate.instant('Would you like to save your changes ?'),
            showThreeButtons: true,
          },
        }
      ).onClose.subscribe((result: boolean | string) => {
        if (result === true) {
          this.save();
          if (this.ref) {
            this.ref.close(null);
          }
        } else if (result === 'no') {
          if (this.ref) {
            this.ref.close(null);
          }
        }
      });
    } else {
      if (this.ref) {
        this.ref.close(null);
      }
    }
  }

  public ngOnDestroy(): void {
    this.formSubscriptions.forEach(sub => sub.unsubscribe());
    this.formSubscriptions = [];
    if (this.backdropClickSubscription) {
      this.backdropClickSubscription.unsubscribe();
      this.backdropClickSubscription = null;
    }
  }
}
